// import components
import type { FieldSelectedSections } from '@mui/x-date-pickers-pro';
import {
  DesktopTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers-pro';
import { CustomTimeOverflowAdornment } from './ControlledTimePicker.components';

// import hooks
import React, { useState } from 'react';

// import utils
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { parseTimeStringToDayjs } from './ControlledTimePicker.utils';
import type { TimePickerProps } from './ControlledTimePicker.types';
import { timeToBaseDate } from './ControlledTimePeriods/ControlledTimePeriods.utils';

export default function ControlledTimePicker({
  control,
  name,
  label,
  variant = 'outlined',
  disabled,
  onChange,
  fullWidth,
  baseDate,
  fixTimeToBaseDate = false,
  referenceDate
}: TimePickerProps) {
  const [typing, setTyping] = useState<FieldSelectedSections | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const timeValue: Dayjs | null =
            fixTimeToBaseDate && baseDate && dayjs(field.value)?.isValid()
              ? timeToBaseDate(field.value, baseDate)
              : field.value;

          return (
            <DesktopTimePicker
              {...field}
              enableAccessibleFieldDOMStructure={false}
              value={timeValue || baseDate}
              referenceDate={referenceDate}
              onAccept={(newValue) => {
                if (dayjs(newValue)?.isValid() && typing === null) {
                  field.onChange(dayjs(newValue));
                  onChange?.(dayjs(newValue));
                }
              }}
              onSelectedSectionsChange={(value) => {
                setTyping(value);
              }}
              label={label}
              ampm={false}
              disabled={disabled}
              slots={{
                inputAdornment: (adornmentProps) => (
                  <CustomTimeOverflowAdornment
                    {...adornmentProps}
                    field={field}
                    time={timeValue ?? null}
                    baseDate={baseDate ?? null}
                  />
                )
              }}
              slotProps={{
                textField: {
                  variant: variant,
                  size: 'small',
                  sx: { width: fullWidth ? '100%' : '140px' },
                  required: true,
                  error: !!fieldState?.error?.type,
                  helperText: fieldState?.error?.message,
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                    const parsedTime = parseTimeStringToDayjs(
                      event.target.value,
                      baseDate
                    );
                    field.onChange(parsedTime);
                    onChange?.(parsedTime);
                  }
                },
                actionBar: {
                  sx: {
                    '&.MuiPickersLayout-actionBar': {
                      paddingTop: (theme) => theme.spacing(4),
                      justifyContent: 'center'
                    }
                  }
                }
              }}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
}
